import Config, { trackingConfig } from '@/shared/Config'
import ConfigDashboard from '@/dashboard/config'
import Vue from 'vue'

/*
 * Analytics middleware backed by the Commanders Act container (Piano Analytics).
 *
 * enable() adds the container (ovh_delta.js) itself, the first time the user grants analytics
 * consent, so refusing users never download it. It runs with the current choices and on every
 * change. Once loaded, the container reads consent itself through window.__cmp('getConsent')
 * and the CMP notifies it of changes (tC.event.giveConsent), so it stops sending on its own if
 * consent is withdrawn.
 *
 * NOTE: the container must not go through the CMP's `scripts` array. The deployed CMP injects
 * those while it boots, before consent and before jQuery is on window (DATATR-3989).
 *
 * Two facts about the container drive the code below:
 * - it has no load-time page view: the landing page is only counted if we send
 *   spa_pageLoad for it too, so every page RouteWrapper displays (its `dpf:page-ready`
 *   event, fired once the page has its data and title) goes through page(), and the ones
 *   displayed before the container is up are replayed once it is;
 * - spa_pageLoad reads its `page_label` event variable and the live window.tc_vars, which
 *   must exist (as strings) before the container script executes.
 */

/*
 * The container's own core calls the global `jQuery` at init (jQuery.isArray / .type / .trim
 * / .each), so it must be on window before the container script runs. It has to stay on 3.x:
 * jQuery 4 removed those helpers. Loaded as a lazy chunk right before the container, for
 * consenting users only; jQuery's UMD build does not set the globals under webpack.
 * TODO: drop this (+ `yarn remove jquery`) once the Piano team ships a vanilla container.
 */
const loadJquery = async () => {
  const { default: jQuery } = await import(/* webpackChunkName: "jquery" */ 'jquery')
  window.jQuery = window.$ = jQuery
}

// Adds the container tag and resolves once the script has run. A failed fetch (offline,
// blocked by an extension) removes the tag again, so a later consent change adds a fresh one.
const loadContainer = (url) => new Promise((resolve, reject) => {
  const script = document.createElement('script')
  script.src = url
  script.onload = () => resolve()
  script.onerror = () => {
    script.remove()
    reject(new Error(`could not load ${url}`))
  }
  document.head.appendChild(script)
})

// Fallback `product_universe` for a module that does not declare TRACKING_UNIVERSE in its
// config/global.js: the category each main.js passes to init()
const SERVICE_NAMES = {
  FPUI: 'core',
  QB: 'am',
  SE: 'databases'
}

/*
 * "Language data" tab of the DT tracking plan, keyed by our UI language codes.
 * TODO: jp is not in the DT table, [JP] / [Japan] is our own guess — to confirm with them.
 */
const LANGUAGE_DATA = {
  en: { env_country: '[US]', env_language: '[WorldEnglish]', interface_language: 'en' },
  fr: { env_country: '[FR]', env_language: '[France]', interface_language: 'fr' },
  jp: { env_country: '[JA]', env_language: '[Japan]', interface_language: 'ja' }
}

/*
 * Data layer skeleton from the DT tracking plan. Everything must be a string: the container
 * reads several keys at init (env_language is .replace()d, user_code is compared to '').
 * The user_* keys are only expected when the information is available, and our session
 * exposes none of it yet (user_code must be an OVH NIC, 0000-0000-00).
 */
const DATALAYER_DEFAULTS = {
  env_template: 'Manager',
  env_currency: '',
  page_theme: 'PublicCloud',
  siteN2_label: 'Manager-PublicCloud',
  siteN2: '',
  goal_type: '',
  user_code: '',
  user_category: '',
  user_profil: '',
  user_email: '',
  user_is_partner: '',
  user_newcustomer: '',
  residential_country: '',
  support_language: '',
  chapter1: '',
  chapter2: '',
  chapter3: ''
}

/*
 * Each route declares its `env_category` from the tracking plan in meta.analytics.category
 * (see any router/routes.js). 'other' is what a route that declares none reports: a handful
 * say nothing on purpose (the embedded third-party screens, the 404), and it is also what a
 * new route gets until someone gives it a category.
 */
const DEFAULT_CATEGORY = 'other'

// Dispatched by src/shared/RouteWrapper.vue once a page is displayed with its data and title
const PAGE_READY_EVENT = 'dpf:page-ready'

// Container steps skipped on each page view reload: only `internalvars` (and the always-on
// data layer normalisation) are recomputed — see sendPage()
const RELOAD_EXCLUSIONS = ['datastorage', 'deduplication', 'privacy', 'eventlisteners']

/*
 * Click chapters, the same values as the page chapters of the tracking plan. The container
 * splits click_label on :: and uses its first three parts as click_chapter1..3, the rest as
 * the click itself ("Click infos" in the Confluence page "Logic Tracking Manager").
 */
const CLICK_CHAPTERS = 'PublicCloud::databases_analytics::data_platform'

// location::format::action[::part][::part], the format src/shared/tests/unit/TrackLabels.spec.js checks
const CLICK_LABEL = /^(page|datagrid|funnel|pop-up|banner)::(button|link|external-link|tile|toggle_on|toggle_off|switch|quantity_more|quantity_less|checkbox|tooltip|range)::[a-z0-9_-]+(::[a-z0-9_-]+){0,2}$/

// Runtime values come from the app: lowercase them, join their words with -, and send
// "unknown" rather than "undefined" or "null" for a missing one
const normalizeLabel = label => String(label).split('::').map(part => part
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9_-]+/g, '-')
  .replace(/(^|_)(undefined|null)(?=_|$)/g, '$1unknown')
).join('::')

const kebab = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

class AnalyticsClient {
  constructor (router, category = 'FPUI', app) {
    this.router = router // Router will be use to watch routechange and send page event
    this.category = category

    this.enabled = false
    // Last page label seen while the container was not up yet, replayed once it is
    this.pendingPage = null
    // Path of the last page counted, see pageReady()
    this.lastPagePath = null

    window.addEventListener(PAGE_READY_EVENT, event => this.pageReady(event.detail))

    Vue.$analytics = app.config.globalProperties.$analytics = this
    app.provide('$analytics', app.config.globalProperties.$analytics)
  }

  /*
   * One page view per displayed page, the landing one included — see the file comment.
   * RouteWrapper fires the event when the page has its data and its final title, which is
   * exactly what Piano must capture; it can fire again for the same page (route watcher,
   * then store data landing), hence the path check.
   */
  pageReady (route) {
    if (!route || route.fullPath === this.lastPagePath) return
    this.lastPagePath = route.fullPath
    this.page(route.name || '', { path: route.fullPath, analytics: route.meta?.analytics })
  }

  // Tracking is off without an https TRACKING_URL (local dev, bare docker build) or when
  // `yarn start:env` disabled telemetry.
  get debug () {
    const { url } = trackingConfig()
    return !url?.startsWith('https://') || process.env.VUE_APP_TELEMETRY_DISABLED === 'true'
  }

  // Developer traces: production consoles stay quiet even with tracking disabled
  get verbose () {
    return this.debug && process.env.VUE_APP_ENV !== 'production'
  }

  // Called with the consent choices at startup and on every change (src/shared/Analytics/
  // index.js); one-shot, loads the container the first time analytics is granted.
  async enable () {
    if (this.enabled) return
    this.enabled = true

    if (this.debug) {
      // eslint-disable-next-line no-console
      if (this.verbose) console.info('Analytics:Container: tracking disabled')
      return
    }

    try {
      // The data layer must be in place before the container script executes. page() may
      // already have recorded the current route, keep it.
      window.tc_vars = { ...this.buildDataLayer(), ...window.tc_vars }
      await loadJquery()
      await loadContainer(trackingConfig().url)
    } catch (error) {
      // A ChunkLoadError after a deploy rotated the hashes, or a container the browser could
      // not fetch: release the guard so a later consent change can try again.
      this.enabled = false
      return console.warn('[Analytics] could not load the tracking dependencies, tracking stays off', error)
    }

    // The container defines its triggers while it runs, before its tag fires load. If they
    // are missing, its own init failed: page() keeps checking, so views resume if it recovers.
    if (!this.containerLoaded()) {
      return console.warn('[Analytics] the tracking container loaded but did not start, page views resume once it does')
    }
    const pending = this.pendingPage
    this.pendingPage = null
    if (pending) this.sendPage(pending)
  }

  containerLoaded () {
    return typeof window.tC?.event?.spa_pageLoad === 'function'
  }

  buildDataLayer () {
    const { siteName, environment } = trackingConfig()
    const route = this.router?.currentRoute?.value
    // Hash history: the route lives in location.hash, not in the pathname
    const path = route?.fullPath || window.location.hash.slice(1)
    return {
      ...DATALAYER_DEFAULTS,
      site_name: siteName || '',
      environment: environment || '',
      env_device: window.navigator.userAgent,
      ...this.languageData(),
      env_category: route?.meta?.analytics?.category || DEFAULT_CATEGORY,
      page_name: this.pendingPage || this.pageLabel(route?.name, path, route?.meta?.analytics)
    }
  }

  // Country / language keys for the language the user picked in the header
  languageData () {
    const lang = window.localStorage.getItem('hq_lang') || 'en'
    return LANGUAGE_DATA[lang] || LANGUAGE_DATA.en
  }

  universe () {
    return trackingConfig().universe || SERVICE_NAMES[this.category] || this.category.toLowerCase()
  }

  /*
   * `product_universe::page`, the DT nomenclature: the universe is the module constant, the
   * page is the route name without its module prefix, slugified — 'Lakehouse Manager - Tables
   * List' -> lakehouse-manager::tables-list, 'CC - Overview (Organization)' ->
   * control-center::overview::organization. A route can set its exact page in
   * meta.analytics.page; route names that are i18n keys fall back to the path segments.
   */
  pageLabel (name, path = '', analytics = {}) {
    const universe = this.universe()
    if (analytics.page) return `${universe}::${analytics.page}`

    let segments
    if (name && !name.includes('.')) {
      const parts = name.split(/\s+-\s+/)
      if (parts.length > 1) parts.shift()
      segments = parts.flatMap(part => part.split(/\s*[()]\s*/))
    } else {
      segments = path.split('?')[0].split('/')
    }
    const page = segments.map(kebab).filter(Boolean).join('::') || 'home'
    return `${universe}::${page}`
  }

  page (name, properties = {}) {
    const analytics = properties.analytics || {}
    const label = this.pageLabel(name, properties.path, analytics)

    if (this.debug) {
      // eslint-disable-next-line no-console
      if (this.verbose) console.info(`Analytics:Page:${label}`)
      return
    }

    // The container reads tc_vars live; the user can switch language at any time
    window.tc_vars = {
      ...window.tc_vars,
      ...this.languageData(),
      page_name: label,
      env_category: analytics.category || DEFAULT_CATEGORY
    }

    if (this.containerLoaded()) return this.sendPage(label)
    this.pendingPage = label
  }

  /*
   * No consent gate here: the container decides from the CMP's choices.
   *
   * The container derives some Piano properties from tc_vars into internal variables
   * (page_category from env_category, for one) when it loads, and a plain tC.event call does
   * not recompute them — every page would report the values of the page the container was
   * loaded on. Reloading the container with only the `internalvars` step enabled refreshes
   * them from the current tc_vars, then fires the event; this container has no load-time
   * tag, so apart from Commanders Act's own sampled usage pixel nothing else runs.
   * Third-party code: a broken tag must not surface as a navigation error.
   */
  sendPage (label) {
    try {
      window.tC.container.reload({
        exclusions: RELOAD_EXCLUSIONS,
        events: { spa_pageLoad: [{}, { page_label: label }] }
      })
    } catch (error) {
      console.warn('[Analytics] spa_pageLoad failed', error)
    }
  }

  /*
   * NOTE: intentionally kept as the app-wide event API even though it currently has no
   * transport — the Segment/King (/v4/fpt) pipeline was removed with the Commanders Act
   * migration, and events will map to the container's triggers (tC.event.spa_click) once
   * the events/click nomenclature is decided with the Piano team.
   */
  async track (event, properties = {}) {
    if (!this.debug) return this.sendClick(event)
    if (!this.verbose) return
    const config = this.category === 'DASHBOARD' ? ConfigDashboard : await Config()
    // Enrich a copy — callers may pass component state, never mutate their object
    const enriched = {
      ...properties,
      project_id: properties.project_id || config?.PROJECT_ID,
      organization_id: properties.organization_id || config?.ORGANIZATION_ID,
      window_size: { height: window.innerHeight, width: window.innerWidth }
    }
    // eslint-disable-next-line no-console
    console.info(`Analytics:Track:${event}`, enriched)
  }

  /*
   * Sends a click to Piano through the container's spa_click trigger. Its click tag checks
   * the analytics consent itself.
   * NOTE: the second argument of track() is not sent. Piano only accepts its listed custom
   * properties, and the calls keep it until the tracking team says how to use it.
   * A click made before the container is loaded is dropped, not replayed.
   */
  sendClick (label) {
    const clickLabel = normalizeLabel(label)
    // Legacy event names, still used by the deprecated ML module, are never sent
    if (!CLICK_LABEL.test(clickLabel)) return
    if (typeof window.tC?.event?.spa_click !== 'function') return
    try {
      window.tC.event.spa_click({}, { click_label: `${CLICK_CHAPTERS}::${clickLabel}` })
    } catch (error) {
      // Third-party code: a broken tag must not surface as an error in the app
      console.warn('[Analytics] spa_click failed', error)
    }
  }

  // Kept for the future user_code / user_category data layer fields. The legacy Segment
  // (traits, options) extra arguments are intentionally ignored.
  identify (email) {
    this.user = { email }
    if (this.verbose) {
      // eslint-disable-next-line no-console
      console.info(`Analytics:Identify:${email}`)
    }
  }
}

export default AnalyticsClient
